<template>
	<div class="resultados_tab report_theme_container" data-theme="resultados">
		<TabHeader
			title="Estado de resultados"
			description="Visualiza el estado de tu negocio en un solo lugar."
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			:compact="true"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange">
			<!-- Modo Cuentas: cada línea del P&L que es una cuenta (Ventas,
				Costos, Gastos, No Operacional) gana un chevron y se abre en sus
				cuentas de nivel 3. Las filas calculadas (márgenes, EBITDA,
				utilidad) no cambian. Mismo control segmentado que Gastos. -->
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button type="button"
						class="filter_btn"
						:class="{ 'is-active': isCuentasMode }"
						:aria-pressed="isCuentasMode ? 'true' : 'false'"
						@click.stop="toggleCuentasMode">
						Cuentas
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Table Section -->
		<div class="underHeader">
			<FinancialResultsTable
				class="mt-4"
				:rows="tableRows"
				:headers="getHeadersTable"
				:isLoading="isLoading"
				:error="error || ''"
				:filterPeriod="filterPeriod"
				:expandedRows="expandedRows"
				:show-period-select="false"
				:cell-tooltips="true"
				navigable
				@toggle-childs="toggleRow"
				@navigate-row="onNavigateRow"
				@navigate-cell="onNavigateCell" />
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";

import TabHeader from "@/components/financialReports/TabHeader.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";
import { TAB_IDS } from "@/views/financialReports/config/tabs";

// El tab destino de cada fila viene en la estructura del backend
// (`targetTab`: el grupo de la cuenta, o Rentabilidad para los márgenes y los
// resultados calculados). Click en una fila salta a su tab; las filas de
// Ventas/Costos/Gastos además abren el modal del mes (esos tabs tienen drill).
const TABS_WITH_DRILL = [TAB_IDS.VENTAS, TAB_IDS.COSTOS, TAB_IDS.GASTOS];

// Filas hijas del modo Cuentas: `acc:<key de la línea>:<cuenta L3>`. Tanto la
// key (`l2:<id>` para cuentas fuera de las canónicas) como el nombre de la
// cuenta pueden llevar ":", así que la línea padre se resuelve buscando la
// key conocida más larga que encabece el resto del id.
const ACCOUNT_ROW_PREFIX = "acc:";

// Las líneas que ya van sangradas (hijas de un total, tema crema) bajan sus
// cuentas L3 un escalón más de sangría para leerse bajo su padre.
function accountThemeFor(row) {
	return row && row.parentKey
		? "theme_row_account is-nested"
		: "theme_row_account";
}

function parentKeyOf(rowId, headers) {
	if (!rowId.startsWith(ACCOUNT_ROW_PREFIX)) return rowId;
	const rest = rowId.slice(ACCOUNT_ROW_PREFIX.length);
	return Object.keys(headers)
		.filter((key) => rest.startsWith(`${key}:`))
		.sort((a, b) => b.length - a.length)[0] || rest.split(":")[0];
}

export default {
	name: "ResultadosTab",
	components: {
		TabHeader,
		FinancialResultsTable
	},
	data() {
		return {
			// Totales con hijas desplegadas (Gastos Totales, No Operacional,
			// Ventas Netas con varias cuentas): la visibilidad de las hijas
			// vive en el store (TOGGLE_CHILD_ROWS por padre).
			expandedParents: [],
			// Líneas del P&L abiertas en sus cuentas (modo Cuentas). Se vacía
			// al apagar el modo.
			expandedAccountRows: [],
			filterOptions: FILTER_PERIOD_OPTIONS
		};
	},
	computed: {
		...mapState("reportsFilters", ["dataFirstMonth", "endMonth", "selectedPeriodRange", "customRange", "customYearRange"]),
		...mapState("financialReports", [
			"filterPeriod",
			"tableRowHeaders",
			"netSellsDetail",
			"maxCostsDetail",
			"maxExpensesDetail",
			"isCuentasMode",
			"accountsDetail",
			"isLoading",
			"error"
		]),
		...mapGetters("financialReports", {
			getHeadersTableFunc: "getHeadersTable"
		}),
		getHeadersTable() {
			return this.getHeadersTableFunc();
		},
		tableRows() {
			const headers = this.tableRowHeaders;
			const rows = [];
			Object.entries(headers)
				// Sólo las filas que el backend marcó para este tipo de empresa
				// (all_keys → isShowed, en el fetch). El estado inicial y los
				// fetch fallidos no traen la marca: sin datos no se pinta
				// NINGUNA fila. Antes (`!== false`) un timeout de /state/
				// dejaba la estructura default completa — una empresa de
				// Servicios veía las filas de Productos.
				.filter(([, row]) => row.isShowed === true)
				.forEach(([key, row]) => {
					// `targetTab` (de la estructura) habilita la afordancia de
					// navegación por fila en la tabla (null = no navegable).
					const targetTab = row.targetTab || null;
					const accounts = this.isCuentasMode ? (this.accountsDetail[key] || []) : [];
					rows.push({
						...row,
						id: key,
						targetTab,
						hasChilds: Boolean(row.hasChilds) || accounts.length > 0,
					});
					if (!this.expandedAccountRows.includes(key)) return;
					// Cuentas L3 de la línea: mismos dicts por cabecera que las
					// líneas (los arma el store), así celdas y tooltips no
					// distinguen padre de hija. Heredan el tab destino del padre.
					// Tema de cuenta (gris, no crema): se diferencian de las
					// hijas de Gastos totales, que son líneas del P&L.
					const accountTheme = accountThemeFor(row);
					accounts.forEach((account) => {
						rows.push({
							id: `${ACCOUNT_ROW_PREFIX}${key}:${account.label}`,
							label: account.label,
							theme: accountTheme,
							isMoney: true,
							itemsDict: account.itemsDict,
							prevYearDict: account.prevYearDict,
							prevPeriodDict: account.prevPeriodDict,
							targetTab,
						});
					});
				});
			return rows;
		},
		expandedRows() {
			return [...this.expandedParents, ...this.expandedAccountRows];
		},
		getNow() {
			return moment().toDate();
		},
		// Rango activo del calendar del header (modo range, igual que Ventas). Si
		// el store tiene `customRange` lo usamos tal cual; si no, derivamos un
		// default (últimos 12 meses sobre endMonth) para que el picker abra
		// pintando "Último año" en vez de "—". Es sólo visual: el fetch sigue
		// usando selectedPeriodRange hasta que el usuario elija un rango.
		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},
		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
		// abra pintando el span real y nunca difiera de la tabla.
		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},
		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción. Incluye customRange
		// y customYearRange para refetchear cuando el usuario mueve el rango.
		fetchSignature() {
			return [
				this.filterPeriod,
				this.endMonth,
				this.selectedPeriodRange,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},
	watch: {
		fetchSignature() {
			this.fetchReportData();
		},
		// Al apagar el modo las líneas vuelven a cerrarse: si el usuario lo
		// vuelve a prender, parte plegado (no con las cuentas de la vez anterior).
		isCuentasMode(isOn) {
			if (!isOn) this.expandedAccountRows = [];
		},
	},
	mounted() {
		this.fetchReportData();
	},
	methods: {
		...mapActions("financialReports", ["fetchReportData", "setCuentasMode"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH", "SET_PENDING_DRILL"]),
		...mapMutations("financialReports", [
			"SET_FILTER_PERIOD",
			"TOGGLE_CHILD_ROWS"
		]),
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		// El calendar del header (modo range) emite el rango elegido; la action
		// sincroniza customRange + selectedPeriodRange="custom" + endMonth.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// El picker de Anual emite el rango de años; estado ortogonal al mes
		// (no toca endMonth/selectedPeriodRange).
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
		toggleCuentasMode() {
			this.setCuentasMode(!this.isCuentasMode);
		},
		// Chevron de una fila. Un total con hijas (Gastos Totales, No
		// Operacional, Ventas Netas con varias cuentas) despliega líneas del
		// P&L cuya visibilidad vive en el store; el resto son líneas abiertas
		// en sus cuentas L3 (modo Cuentas), estado local.
		toggleRow(rowId) {
			const row = this.tableRowHeaders[rowId];
			if (row && row.hasChilds) {
				const isExpanded = !this.expandedParents.includes(rowId);
				this.expandedParents = isExpanded
					? [...this.expandedParents, rowId]
					: this.expandedParents.filter((id) => id !== rowId);
				this.TOGGLE_CHILD_ROWS({ parentKey: rowId, isExpanded });
				return;
			}
			const idx = this.expandedAccountRows.indexOf(rowId);
			if (idx === -1) {
				this.expandedAccountRows = [...this.expandedAccountRows, rowId];
			} else {
				this.expandedAccountRows = this.expandedAccountRows.filter((id) => id !== rowId);
			}
		},
		// Cambia el tab activo vía query param (la URL es la fuente de verdad del
		// tab activo; DashboardReports lee $route.query.tab). No-op si ya estamos
		// en ese tab.
		goToTab(tab) {
			if (this.$route.query.tab === tab) return;
			this.$router
				.replace({ query: { ...this.$route.query, tab } })
				.catch((err) => { if (err.name !== "NavigationDuplicated") throw err; });
		},
		// Click en el nombre de una fila → sólo salta al tab de esa fila. Una
		// cuenta L3 (modo Cuentas) navega como su línea padre.
		targetTabOf(rowId) {
			const row = this.tableRowHeaders[parentKeyOf(rowId, this.tableRowHeaders)];
			return (row && row.targetTab) || null;
		},
		onNavigateRow({ rowId }) {
			const tab = this.targetTabOf(rowId);
			if (tab) this.goToTab(tab);
		},
		// Click en una celda de valor → salta al tab y, si ese tab tiene modal de
		// drill (Ventas/Costos) y estamos en granularidad mensual, deja un
		// pendingDrill { tab, monthLabel } que el tab destino consume al montar
		// para abrir el modal de ese mes. En trimestral/anual la celda agrupa
		// varios meses, así que sólo navega.
		onNavigateCell({ rowId, monthLabel }) {
			const tab = this.targetTabOf(rowId);
			if (!tab) return;
			if (this.filterPeriod === "month" && TABS_WITH_DRILL.includes(tab)) {
				this.SET_PENDING_DRILL({ tab, monthLabel });
			}
			this.goToTab(tab);
		},
	},
};
</script>

<style scoped lang="scss">
.resultados_tab {
	position: relative;
	// Respiro inferior para que la card no quede pegada al borde del viewport
	// (el sistema pide "denso pero respirable", no apretado).
	padding-bottom: 24px;
	height: 100%;
}

// Header común vive en TabHeader.vue. Acá solo el SCSS del control segmentado
// del slot #center-sub-nav (botón Cuentas). Replicado de Gastos porque
// TabHeader es scoped y sus estilos no llegan al markup inyectado al slot.

.sub_nav_filters {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 0 2px;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.has-gap-cards {
	gap: 16px;
}
</style>
